import { db, type OrderLine } from './db'
import { isDigital } from './pricing'
import { localCatalog } from '../data/catalog'
import { policy } from '../data/policies'
import { today } from '../core/format/dates'
import { isKuurProductId, kuurValidUntil } from '../core/rules/kuur'
import type { Course } from '../core/types/account'

/**
 * DEMO — een betaalde bestelling met een kuur zet die kuur op het account (met Mollie: bij de
 * betaalde webhook). Zelfde regels als de server (docs/LARAVEL.md §10, kuursessies):
 * - geen kuur, of een afgeronde kuur → nieuwe kuur, vooruit betaald, 0 gedaan;
 * - lopende kuur van dezelfde behandeling, per keer betaald → wordt vooruit betaald (opnieuw vanaf 0:
 *   eerder los betaalde sessies tellen niet mee);
 * - anders (andere lopende kuur, al vooruit betaald, of geen account) → niets automatisch; de bestelling
 *   staat in de inbox en Sarah legt hem vast in het klantdossier.
 * Geeft terug of de kuur is gekoppeld.
 */
/**
 * Sarah legt een vooruitbetaalde kuur vast in het dossier: de bestellingen van deze klant met een
 * nog niet gekoppelde kuur van die behandeling zijn daarmee afgehandeld (melding weg; alleen kuur = afgerond).
 */
export function resolveKuurOrders(customerId: string, course: Course | null) {
  if (!course?.prepaid) return
  const id = `kuur-${course.treatmentSlug}`
  db.orders()
    .filter((o) => o.customerId === customerId && o.kuurUnlinked && o.lines.some((l) => l.productId === id))
    .forEach((o) =>
      db.changeOrder(o.id, (row) => ({
        ...row,
        kuurUnlinked: undefined,
        fulfilment: row.fulfilment === 'nieuw' && row.lines.every((l) => isDigital(l.productId)) ? 'bezorgd' : row.fulfilment,
      })),
    )
}

/**
 * Een gast die later een account maakt: haar betaalde gastbestellingen met een kuur die nog niet
 * gekoppeld is, komen op dat account (docs/KASSA-ABONNEMENTEN.md, "kuur, rest"). Alleen op een
 * bevestigd e-mailadres (inloglink of bevestigingslink), of `orderIds` die ze net zelf claimde
 * (de bedankpagina, met het token van die bestelling). Lukt koppelen niet (er loopt al een andere
 * kuur), dan blijft de melding voor Sarah staan, nu met het account erbij.
 * Geeft het aantal gekoppelde kuren terug.
 */
export function linkGuestKuren(customerId: string, email: string, orderIds: readonly string[] = []): number {
  const mail = email.trim().toLowerCase()
  const candidates = db
    .orders()
    .filter((o) => o.kuurUnlinked && o.payment.status === 'betaald')
    .filter((o) => orderIds.includes(o.id) || (!o.customerId && o.request.email.trim().toLowerCase() === mail))
    .sort((a, b) => a.receivedAt.localeCompare(b.receivedAt))
  let linked = 0
  for (const o of candidates) {
    const attached = attachOrderKuur({ customerId, lines: o.lines })
    db.changeOrder(o.id, (row) => ({
      ...row,
      customerId,
      ...(attached
        ? { kuurUnlinked: undefined, fulfilment: row.fulfilment === 'nieuw' && row.lines.every((l) => isDigital(l.productId)) ? 'bezorgd' : row.fulfilment }
        : {}),
    }))
    if (attached) linked += 1
  }
  return linked
}

export function attachOrderKuur(order: { customerId?: string; lines: OrderLine[] }): boolean {
  const line = order.lines.find((l) => isKuurProductId(l.productId))
  if (!line || !order.customerId) return false
  return attachKuur(order.customerId, line.productId.slice('kuur-'.length), line.price)
}

/** Kan er een nieuwe vooruitbetaalde kuur van `slug` op dit account? Geen kuur, een afgeronde, of dezelfde per keer. */
export function canAttachKuur(course: Course | null | undefined, slug: string): boolean {
  if (!course || course.done >= course.total) return true
  return course.treatmentSlug === slug && !course.prepaid
}

/**
 * Zet een vooruitbetaalde kuur op het account (webshop en kassa): `paid` in euro's. `false` als dat
 * niet automatisch kan (zie `canAttachKuur`) of de behandeling geen kuur heeft.
 */
export function attachKuur(customerId: string, slug: string, paid: number): boolean {
  const kuur = localCatalog.treatments.find((t) => t.slug === slug)?.kuur
  if (!kuur) return false
  const paidOn = today()
  const fresh: Course = {
    treatmentSlug: slug,
    total: kuur.sessions,
    done: 0,
    prepaid: { paid, paidOn, validUntil: kuurValidUntil(paidOn, db.savedPolicy()?.kuurValidMonths ?? policy.kuurValidMonths) },
  }
  return db.changeAccount(customerId, (a) => (canAttachKuur(a.course, slug) ? [{ ...a, course: fresh }, true] : [a, false])) ?? false
}
